import { useMemo } from "react";
import { useEarlyBirdsCounter } from "@/shared";
import { EARLY_BIRD_PLANS } from "@/shared/constants";
import { usePurchaseSubscriptionStoreShallow } from "../store";
import { getSelectedPlan } from "../utils";

// Constants for number formatting
const NUMBER_FORMAT_OPTIONS = {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
} as const;

// Constants for discount calculations
const DISCOUNT_RATES = {
  earlyBirds: 0.6,
  regular: 0.4,
} as const;

interface CheckoutDetailsProps {
  plan: string;
  period: string;
}

interface FormattedDetails {
  name: string;
  quantity: number;
  net: string;
  net_after_discount: string;
  tax_after_discount: string;
  gross_after_discount: string;
  gross: string;
  gross_discount: string;
  monthlyPrice: string;
  billingPeriod: string;
  subtotal: string;
  annualDiscount: string;
  discountedSubtotal: string;
  taxValue: number;
  discountPercentage: number;
  currency: string;
}

const useCheckoutDetails = ({ plan, period }: CheckoutDetailsProps) => {
  const { isEarlyBirds } = useEarlyBirdsCounter();
  const checkoutDetails = usePurchaseSubscriptionStoreShallow(
    (state) => state.checkoutDetails,
  );

  const details = useMemo((): FormattedDetails | null => {
    if (!checkoutDetails) return null;

    const withEarlyBirdsDiscount =
      EARLY_BIRD_PLANS.includes(plan) && isEarlyBirds;

    const { eventData } = checkoutDetails;
    const { product, checkout } = eventData;
    const { customer } = checkout.prices;
    const { items, currency } = customer;
    const { display_price } = items[0];
    const { line_price, tax_rate } = display_price;
    const { name, quantity } = product;
    const {
      net,
      net_after_discount,
      tax_after_discount,
      gross_after_discount,
      gross,
      gross_discount,
    } = line_price;

    const monthlyPrice = getSelectedPlan("Monthly", plan)?.price ?? 0;
    const discountRate = withEarlyBirdsDiscount
      ? DISCOUNT_RATES.earlyBirds
      : DISCOUNT_RATES.regular;
    const monthsMultiplier = period === "annually" ? 12 : 1;

    const subtotal = monthlyPrice * quantity * monthsMultiplier;
    const annualDiscount = subtotal * discountRate;
    const discountedSubtotal = subtotal - annualDiscount;

    const formatNumber = (value: number) =>
      value.toLocaleString("en-US", NUMBER_FORMAT_OPTIONS);

    return {
      name,
      quantity,
      net: formatNumber(net),
      net_after_discount: formatNumber(net_after_discount),
      tax_after_discount: formatNumber(tax_after_discount),
      gross_after_discount: formatNumber(gross_after_discount),
      gross: formatNumber(gross),
      gross_discount: formatNumber(gross_discount),
      monthlyPrice: formatNumber(monthlyPrice),
      billingPeriod: period === "annually" ? "12 months" : "1 month",
      subtotal: formatNumber(subtotal),
      annualDiscount: formatNumber(annualDiscount),
      discountedSubtotal: formatNumber(discountedSubtotal),
      taxValue: tax_rate ? tax_rate * 100 : 0,
      discountPercentage: discountRate * 100,
      currency: currency === "EUR" ? "€" : "$",
    };
  }, [checkoutDetails, period, isEarlyBirds, plan]);

  return { details };
};

export default useCheckoutDetails;
